import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getStoredToken } from '../../utils/tokenStorage';

interface SharingPolicy {
  tenant_name: string;
  admin_name: string | null;
  allow_external_sharing: boolean;
  allowed_share_domains: string[];
  domain_restricted: boolean;
  require_otp: boolean;
  watermarking_enabled: boolean;
}

interface ShareSecurityBriefingProps {
  isOpen: boolean;
  onContinue: () => void;
  onCancel: () => void;
}

const ShareSecurityBriefing: React.FC<ShareSecurityBriefingProps> = ({ isOpen, onContinue, onCancel }) => {
  const { branding } = useAuth();
  const [policy, setPolicy] = useState<SharingPolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError('');

    const token = getStoredToken();
    fetch('/api/v1/sharing-policy', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error('Failed to load policy');
        return r.json();
      })
      .then((data) => {
        setPolicy(data);
        setLoading(false);
      })
      .catch(() => {
        setError('Could not load sharing policy');
        setLoading(false);
      });
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onCancel}>
      <div
        className="bg-slate-900 border border-white/10 rounded-xl w-full max-w-lg p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-400 text-sm mb-4">{error}</p>
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors"
            >
              Close
            </button>
          </div>
        ) : policy ? (
          <>
            {/* Header */}
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{
                background: `linear-gradient(135deg, ${branding.primary_color} 0%, ${branding.secondary_color} 100%)`
              }}>
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Secure Sharing</h3>
                <p className="text-sm text-slate-400">{policy.tenant_name} Policy</p>
              </div>
            </div>

            <p className="text-sm text-slate-400 mb-4">
              Your organisation's sharing policy:
            </p>

            {/* Security layers */}
            <div className="space-y-3 mb-5">
              <div className="flex items-start gap-3">
                <span className="text-teal-400 mt-0.5 flex-shrink-0">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </span>
                <div>
                  <p className="text-sm text-white font-medium">Email-pinned</p>
                  <p className="text-xs text-slate-400">Only the specific recipient can open</p>
                </div>
              </div>

              {policy.require_otp && (
                <div className="flex items-start gap-3">
                  <span className="text-teal-400 mt-0.5 flex-shrink-0">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <div>
                    <p className="text-sm text-white font-medium">Identity verified</p>
                    <p className="text-xs text-slate-400">Recipient must enter a code sent to their email</p>
                  </div>
                </div>
              )}

              {policy.watermarking_enabled && (
                <div className="flex items-start gap-3">
                  <span className="text-teal-400 mt-0.5 flex-shrink-0">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <div>
                    <p className="text-sm text-white font-medium">Watermarked</p>
                    <p className="text-xs text-slate-400">All views are stamped with recipient email and timestamp</p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <span className="text-teal-400 mt-0.5 flex-shrink-0">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </span>
                <div>
                  <p className="text-sm text-white font-medium">Fully audited</p>
                  <p className="text-xs text-slate-400">Every access is logged and visible to you and your administrator</p>
                </div>
              </div>

              {/* Domain restriction - dynamic */}
              {policy.domain_restricted ? (
                <div className="flex items-start gap-3">
                  <span className="text-teal-400 mt-0.5 flex-shrink-0">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <div>
                    <p className="text-sm text-white font-medium">Domain restricted</p>
                    <p className="text-xs text-slate-400">
                      Sharing limited to {policy.allowed_share_domains.map(d => `@${d}`).join(', ')}
                    </p>
                  </div>
                </div>
              ) : policy.allow_external_sharing ? (
                <div className="flex items-start gap-3">
                  <span className="text-amber-400 mt-0.5 flex-shrink-0">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <div>
                    <p className="text-sm text-white font-medium">External sharing enabled</p>
                    <p className="text-xs text-slate-400">You can share outside your organisation. All external access is logged.</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <span className="text-teal-400 mt-0.5 flex-shrink-0">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <div>
                    <p className="text-sm text-white font-medium">Internal only</p>
                    <p className="text-xs text-slate-400">Sharing is restricted to members of your organisation</p>
                  </div>
                </div>
              )}
            </div>

            {/* Admin notice */}
            <p className="text-xs text-slate-500 mb-6">
              Your administrator{policy.admin_name ? ` (${policy.admin_name})` : ''} can view all share activity for {policy.tenant_name}.
            </p>

            {/* Buttons */}
            <div className="flex justify-end gap-3">
              <button
                onClick={onCancel}
                className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onContinue}
                className="px-5 py-2 text-sm font-medium text-white rounded-lg transition-all flex items-center gap-2"
                style={{
                  background: `linear-gradient(135deg, ${branding.primary_color} 0%, ${branding.secondary_color} 100%)`
                }}
              >
                Continue to Share
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default ShareSecurityBriefing;
